import { memo, useMemo } from 'react';
import { createIconElement, formatCurrency, formatNumber, getLocalTimezone } from '@/lib';
import { useUser } from '@/queries';
import { useAffiliateMetrics, useAffiliates } from '../../queries/affiliateAnalyticsQueries';
import IcMoney from '@/assets/icons/ic-currency-dollar-circle.svg';
import IcTraffic from '@/assets/icons/ic-switch-horizontal.svg';
import IcCommunity from '@/assets/icons/ic-community.svg';
import { StanCard, StanLoader } from '@/components';

/**
 * AffiliateMetricsCards - Displays key metrics at the top of the analytics page.
 * Manages its own data fetching for complete encapsulation.
 * Memoized to prevent unnecessary re-renders when table filters/pagination change.
 */
export const AffiliateMetricsCards = memo(function AffiliateMetricsCards({ pageId }: { pageId: string }) {
    const { user } = useUser();
    const { data: metricsData, isLoading: isMetricsLoading } = useAffiliateMetrics(pageId);

    // Fetch unfiltered affiliates for accurate Total Affiliates count
    const { data: unfilteredAffiliatesData } = useAffiliates(pageId, {
        page: 1,
        timezone: getLocalTimezone(),
    });

    // Merge metrics data with accurate total resellers count from affiliates API
    // Memoized to maintain stable object reference and prevent unnecessary re-renders
    const metrics = useMemo(() => {
        if (!metricsData) return undefined;

        return {
            ...metricsData,
            total_resellers: unfilteredAffiliatesData?.metadata.totalCustomers ?? metricsData.total_resellers,
        };
    }, [metricsData, unfilteredAffiliatesData?.metadata.totalCustomers]);

    if (isMetricsLoading || !metrics) {
        return (
            <StanCard className='!p-0'>
                <div className='flex h-48 items-center justify-center'>
                    <StanLoader />
                </div>
            </StanCard>
        );
    }

    return (
        <StanCard className='!p-0'>
            <div className='flex w-full flex-wrap justify-around md:flex-nowrap md:gap-4 md:p-6'>
                <div className='border-neutral-soft flex w-full flex-col justify-between border p-6 max-md:border-t-0 max-md:border-r-0 max-md:border-b max-md:border-l-0 max-md:pb-6 md:rounded-lg'>
                    <small className='text-gray flex items-center gap-1 text-sm'>
                        {createIconElement(IcMoney, { className: 'size-6 text-gray' })} Affiliate Income
                    </small>
                    <span className='text-accent-strong mt-1 text-4xl font-bold lg:text-4xl'>
                        {formatCurrency(metrics.total_earnings, user?.data.currency)}
                    </span>
                </div>
                <div className='border-neutral-soft flex w-1/2 flex-col justify-between p-6 md:w-full md:rounded-lg md:border'>
                    <small className='text-gray flex items-center gap-1 text-sm'>
                        {createIconElement(IcTraffic, { className: 'size-6 text-gray' })} Total Sales
                    </small>
                    <span className='text-primary-dark mt-1 text-4xl font-bold lg:text-4xl'>
                        {formatNumber(metrics.total_sales, { decimals: 0 })}
                    </span>
                </div>
                <div className='rounded-10 border-neutral-soft flex w-1/2 flex-col justify-between border-l p-6 md:w-full md:rounded-lg md:border'>
                    <small className='text-gray flex items-center gap-1 text-sm'>
                        {createIconElement(IcCommunity, { className: 'size-6 text-gray' })} Total Affiliates
                    </small>
                    <span className='text-primary-dark mt-1 text-4xl font-bold lg:text-4xl'>
                        {formatNumber(typeof metrics.total_resellers === 'number' ? metrics.total_resellers : 0, {
                            decimals: 0,
                        })}
                    </span>
                </div>
            </div>
        </StanCard>
    );
});
